/**
 * Per-type format validators for IPE question inputs.
 *
 * Each validator returns null if valid, or an error message string if invalid.
 * These only check FORMAT — the "required" check is handled separately.
 */

type Validator = (value: unknown) => string | null;

function validatePhone(value: unknown): string | null {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (digits.length < 10) {
    return 'Please enter a valid phone number (at least 10 digits).';
  }
  return null;
}

function validateEmail(value: unknown): string | null {
  const str = String(value ?? '').trim();
  // Basic check: has @, has domain with dot
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailPattern.test(str)) {
    return 'Please enter a valid email address.';
  }
  return null;
}

function validateSSN(value: unknown): string | null {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (digits.length !== 9) {
    return 'Please enter a valid 9-digit Social Security Number.';
  }
  return null;
}

function validateLastSSN(value: unknown): string | null {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (digits.length !== 4) {
    return 'Please enter the last 4 digits of your SSN.';
  }
  return null;
}

function validateZipCode(value: unknown): string | null {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (digits.length !== 5 && digits.length !== 9) {
    return 'Please enter a valid zip code (5 or 9 digits).';
  }
  return null;
}

function validateCurrency(value: unknown): string | null {
  const str = String(value ?? '').replace(/[,$\s]/g, '');
  const num = Number(str);
  if (isNaN(num)) {
    return 'Please enter a valid dollar amount.';
  }
  if (num < 0) {
    return 'Amount cannot be negative.';
  }
  return null;
}

function validateNumber(value: unknown): string | null {
  const str = String(value ?? '').replace(/[,\s]/g, '');
  if (isNaN(Number(str))) {
    return 'Please enter a valid number.';
  }
  return null;
}

function validatePercentage(value: unknown): string | null {
  const str = String(value ?? '').replace(/[%\s]/g, '');
  const num = Number(str);
  if (isNaN(num)) {
    return 'Please enter a valid percentage.';
  }
  if (num < 0 || num > 100) {
    return 'Percentage must be between 0 and 100.';
  }
  return null;
}

function validateDate(value: unknown): string | null {
  const str = String(value ?? '').trim();
  const date = new Date(str);
  if (isNaN(date.getTime())) {
    return 'Please enter a valid date.';
  }
  return null;
}

const VALIDATORS: Record<string, Validator> = {
  phone: validatePhone,
  email: validateEmail,
  ssn: validateSSN,
  last_SSN: validateLastSSN,
  zip_code: validateZipCode,
  currency: validateCurrency,
  number: validateNumber,
  log_number: validateNumber,
  percentage: validatePercentage,
  date: validateDate,
};

/**
 * Validates a value for a given question type.
 * Returns null if valid (or no validator exists for the type), error message otherwise.
 * Skips validation if value is empty (the "required" check handles that separately).
 */
export function validateFormat(
  questionType: string,
  value: unknown
): string | null {
  // Skip format validation on empty values — the required check handles that
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const validator = VALIDATORS[questionType];
  if (!validator) {
    return null;
  }

  return validator(value);
}
